import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Lock as LockIcon,
  Check as CheckIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useSelector } from "react-redux";
import { API_URL, createAxiosInstance } from "../../../api";
// @ts-ignore - SVG import
import successIcon from "../../../assets/success-icon.svg";
// @ts-ignore - PNG import
import stripeLogo from "../../../assets/stripelogo.png";

interface TrialStatus {
  onTrial: boolean;
  trialEndDate: string;
  daysRemaining: number;
  isExpired: boolean;
}

export default function Subscription({ onDirtyChange, onValidChange, onSave }: any) {
  const stripe = useStripe();
  const elements = useElements();
  const userId = useSelector((state: any) => state.main.userId);
  
  const [paid, setPaid] = useState(false);
  const [paidStatus, setPaidStatus] = useState("false");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [cancelledInfo, setCancelledInfo] = useState<{ cancelAtPeriodEnd: boolean; activeUntil: string } | null>(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!userId) {
        console.log("User ID is undefined or invalid. Skipping payment check.");
        return;
      }

      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;

        if (response.data && isPaid) {
          setPaid(true);
          setPaidStatus(response.data.paid);
          if (response.data.cancelAtPeriodEnd) {
            setCancelledInfo({
              cancelAtPeriodEnd: true,
              activeUntil: response.data.activeUntil,
            });
          }
        } else {
          setPaid(false);
          setPaidStatus("false");
        }

        // Check if user is on trial
        if (response.data?.onTrial) {
          try {
            const trialResponse = await axiosInstance.get(
              `${API_URL}/api/billings/trial/status/${userId}`
            );
            if (trialResponse.data?.success && trialResponse.data?.hasTrial) {
              setTrialStatus({
                onTrial: true,
                trialEndDate: trialResponse.data.trialEndDate,
                daysRemaining: trialResponse.data.daysRemaining,
                isExpired: trialResponse.data.isExpired,
              });
              // If on active trial, mark as paid
              if (!trialResponse.data.isExpired) {
                setPaid(true);
                setPaidStatus("Paid");
              }
            }
          } catch (trialError) {
            console.error("Error fetching trial status:", trialError);
          }
        }
      } catch (error: any) {
        console.error(
          "Error checking payment status:",
          error.response?.data?.message || error.message
        );
        setPaidStatus("false");
      }
    };

    checkPaymentStatus();
    onDirtyChange?.(false);
  }, [userId]);

  const cardElementStyles = {
    base: {
      backgroundColor: "light-grey",
      fontSize: "18px",
      color: "#32325d",
      "::placeholder": {
        color: "#aab7c4",
      },
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "10px",
      marginBottom: "16px",
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
    focus: {
      borderColor: "#712d93",
    },
  };

  const handlePayment = async () => {
    if (!stripe || !elements) {
      Swal.fire({
        icon: "error",
        title: "Payment System Loading",
        text: "Please wait while the payment system initializes",
      });
      return;
    }
    setPaymentLoading(true);

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Card element is not available.",
      });
      setPaymentLoading(false);
      return;
    }

    try {
      const { error, token } = await stripe.createToken(cardNumberElement);

      if (error) {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: error.message,
        });
        setPaymentLoading(false);
        return;
      }

      const axiosInstance = createAxiosInstance();

      if (!firstName.trim() || !lastName.trim()) {
        Swal.fire({
          icon: "error",
          title: "Missing Information",
          text: "Please enter both first and last name",
        });
        setPaymentLoading(false);
        return;
      }

      const response = await axiosInstance.post(`${API_URL}/api/billings/stripe`, {
        token: token.id,
        subscriptionType,
        name: `${firstName} ${lastName}`,
        userId,
      });

      // Handle 3D Secure authentication if required
      if (response.data?.requiresAction && response.data?.clientSecret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.data.clientSecret
        );

        if (confirmError || paymentIntent?.status !== "succeeded") {
          // Clean up the incomplete subscription in Stripe
          if (response.data?.subscriptionId) {
            try {
              await axiosInstance.post(`${API_URL}/api/billings/cancel-incomplete`, {
                subscriptionId: response.data.subscriptionId,
              });
            } catch (cleanupErr) {
              console.error("Failed to clean up incomplete subscription:", cleanupErr);
            }
          }
          throw new Error(confirmError?.message || "Payment authentication was not completed successfully");
        }

        console.log("3D Secure authentication completed successfully");
      }

      if (response.data && (response.data.success || response.data.requiresAction)) {
        Swal.fire({
          imageUrl: successIcon,
          imageHeight: "100px",
          title: "Payment Successful",
          text: "Your subscription has been activated!",
          confirmButtonColor: "#712d94",
          customClass: {
            icon: "no-before-icon",
          },
        }).then(() => {
          setPaid(true);
          setPaidStatus("Paid");
          onDirtyChange?.(false);
          onSave?.();
          // Dispatch event to notify other components
          window.dispatchEvent(new Event('paymentCompleted'));
        });
      } else {
        throw new Error(response.data.message || "Payment failed");
      }
    } catch (error: any) {
      console.error("Payment processing error:", error);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          error.response?.data?.message ||
          "There was an issue processing your payment.",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    // Notify parent of validation status
    const isValid = paidStatus !== "false";
    onValidChange?.(isValid);
  }, [paidStatus, onValidChange]);

  (Subscription as any).save = async () => {
    // If user already has active subscription, there's nothing to save
    // Just mark as not dirty and return
    if (paidStatus !== "false") {
      onDirtyChange?.(false);
      return;
    }
    // If payment form is visible, mark as dirty if user has interacted with it
    onDirtyChange?.(false);
  };

  const monthlyFeatures = ["Flexible", "Cancel anytime"];
  const yearlyFeatures = [
    "Save 17%",
    "Cancel anytime",
  ];

  return (
    <Box>
      <Typography 
        variant="h2"
        sx={{ 
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
          Choose Your Payment Plan
        </Box>
        

      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Plan & payment
        </Typography>
      </Box>

      {trialStatus?.onTrial && !trialStatus?.isExpired ? (
        <Box sx={{ 
          p: 3, 
          backgroundColor: '#d1ecf1', 
          border: '1px solid #bee5eb',
          borderRadius: 2,
          mb: 4,
        }}>
          <Typography variant="h2" sx={{ mb: 1, color: '#0c5460' }}>
            You're on a Free Trial
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#0c5460' }}>
            Your free trial is active until {new Date(trialStatus.trialEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
            You can skip this step for now and subscribe later.
          </Typography>
        </Box>
      ) : paidStatus === "false" ? (
        <>
          <Typography
            variant="h5"
            align="center"
            sx={{ mb: 4, fontWeight: 600, color: "#712d94" }}
          >
            Support our platform and grow your tutoring business.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
        {/* Monthly Plan */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: subscriptionType === "monthly" ? "#712d94" : "#ffffff",
              border: subscriptionType === "monthly" 
                ? "2px solid #712d94" 
                : "1px solid #e9e9ec",
              borderRadius: 3,
              height: "100%",
              cursor: "pointer",
              transition: "all 0.2s ease",
              overflow: "visible",
              "&:hover": {
                borderColor: "#712d94",
                boxShadow: subscriptionType === "monthly" 
                  ? "0 4px 16px rgba(113, 45, 148, 0.25)" 
                  : "0 4px 12px rgba(113, 45, 148, 0.15)",
                transform: "translateY(-2px)",
              },
            }}
            onClick={() => {
              setSubscriptionType("monthly");
              onDirtyChange?.(true);
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ 
                    mb: 0.5, 
                    fontWeight: 600, 
                    color: subscriptionType === "monthly" ? "rgba(255, 255, 255, 0.9)" : "#64748b",
                    fontSize: "0.875rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Monthly
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ 
                    fontWeight: 700, 
                    color: subscriptionType === "monthly" ? "#fff" : "#1e293b",
                    lineHeight: 1.2,
                  }}
                >
                  £2.00
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: subscriptionType === "monthly" ? "rgba(255, 255, 255, 0.85)" : "#64748b",
                    fontSize: "0.875rem",
                    mt: 0.5,
                  }}
                >
                  (Roughly 7p a day!)
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                {monthlyFeatures.map((feature, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: "flex", 
                      alignItems: "flex-start", 
                      gap: 1.5,
                    }}
                  >
                    <CheckIcon 
                      sx={{ 
                        color: subscriptionType === "monthly" ? "#fff" : "#712d94", 
                        fontSize: "20px",
                        mt: 0.25,
                        flexShrink: 0,
                      }} 
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: subscriptionType === "monthly" ? "#fff" : "#334155",
                        fontWeight: subscriptionType === "monthly" ? 500 : 400,
                        lineHeight: 1.5,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Yearly Plan */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: subscriptionType === "yearly" ? "#712d94" : "#ffffff",
              border: subscriptionType === "yearly" 
                ? "2px solid #712d94" 
                : "1px solid #e9e9ec",
              borderRadius: 3,
              height: "100%",
              cursor: "pointer",
              transition: "all 0.2s ease",
              position: "relative",
              overflow: "visible",
              "&:hover": {
                borderColor: "#712d94",
                boxShadow: subscriptionType === "yearly" 
                  ? "0 4px 16px rgba(113, 45, 148, 0.25)" 
                  : "0 4px 12px rgba(113, 45, 148, 0.15)",
                transform: "translateY(-2px)",
              },
            }}
            onClick={() => {
              setSubscriptionType("yearly");
              onDirtyChange?.(true);
            }}
          >
            <Chip
              icon={<StarIcon sx={{ fontSize: "16px !important" }} />}
              label="Best Value"
              sx={{
                position: "absolute",
                top: -12,
                right: 16,
                backgroundColor: "#FFC107",
                color: "#000",
                fontWeight: 700,
                fontSize: "0.75rem",
                height: "32px",
                px: 1,
                boxShadow: "0 2px 8px rgba(255, 193, 7, 0.3)",
                zIndex: 1,
                "& .MuiChip-icon": {
                  color: "#000",
                  marginLeft: "8px",
                },
                "& .MuiChip-label": {
                  paddingLeft: "4px",
                  paddingRight: "8px",
                },
              }}
            />
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: subscriptionType === "yearly" ? "rgba(255, 255, 255, 0.9)" : "#64748b",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Yearly
                  </Typography>
                  {subscriptionType !== "yearly" && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    >
                      (Save 17%)
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: subscriptionType === "yearly" ? "#fff" : "#1e293b",
                    lineHeight: 1.2,
                  }}
                >
                  £19.99
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                {yearlyFeatures.map((feature, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: "flex", 
                      alignItems: "flex-start", 
                      gap: 1.5,
                    }}
                  >
                    <CheckIcon
                      sx={{
                        color: subscriptionType === "yearly" ? "#fff" : "#712d94",
                        fontSize: "20px",
                        mt: 0.25,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: subscriptionType === "yearly" ? "#fff" : "#334155",
                        fontWeight: subscriptionType === "yearly" ? 500 : 400,
                        lineHeight: 1.5,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 4, color: "#6b7280", maxWidth: "800px", mx: "auto" }}
          >
            We charge a small subscription fee to cover website hosting, secure payments, and
            continuous improvements - so we can connect you with more students and keep
            everything running smoothly.
          </Typography>

          <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 3,
            }}
          >
            <LockIcon sx={{ color: "#712d94", fontSize: "20px" }} />
            <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
              Payment Details (Secure via Stripe)
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  onDirtyChange?.(true);
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  onDirtyChange?.(true);
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <CardNumberElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <CardExpiryElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <CardCvcElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handlePayment}
              loading={paymentLoading}
              disabled={!stripe || paymentLoading || !firstName || !lastName}
              sx={{
                minWidth: "200px",
                height: "48px",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Pay Now
            </LoadingButton>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <img
              src={stripeLogo}
              alt="Powered by Stripe"
              style={{ maxWidth: "150px", height: "auto" }}
            />
          </Box>
        </CardContent>
      </Card>

          <Typography
            variant="h2"
            align="center"
            sx={{ mt: 3, color: "#1a1a1a", fontStyle: "italic", fontWeight: 700 }}
          >
            Join{" "}
            <Box component="span" sx={{ color: "#712d94" }}>
              400+ tutors
            </Box>{" "}
            already building their profiles with us.
          </Typography>
        </>
      ) : (
        <Card
          elevation={0}
          sx={{
            border: `2px solid ${cancelledInfo ? "#f59e0b" : "#24a148"}`,
            borderRadius: 2,
            backgroundColor: cancelledInfo ? "rgba(245, 158, 11, 0.05)" : "rgba(36, 161, 72, 0.05)",
            my: 3,
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: cancelledInfo ? "rgba(245, 158, 11, 0.1)" : "rgba(36, 161, 72, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckIcon sx={{ fontSize: 48, color: cancelledInfo ? "#f59e0b" : "#24a148" }} />
              </Box>
            </Box>
            <Typography variant="h2" sx={{ mb: 4, fontWeight: 700, color: cancelledInfo ? "#f59e0b" : "#24a148" }}>
              {cancelledInfo ? "Plan Cancelled" : "Payment Plan is Active"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "#6b7280", textAlign: "center" }}>
              {cancelledInfo
                ? `Your plan has been cancelled but remains active until ${new Date(cancelledInfo.activeUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. After that, your profile will no longer be visible to students.`
                : "Your payment plan is active and you're all set! You can continue to the next step."}
            </Typography>
            <Typography variant="body2" sx={{ color: "#9ca3af", fontStyle: "italic", textAlign: "center" }}>
              {cancelledInfo
                ? "You can resubscribe anytime from the Payment Plans page."
                : "Thank you for supporting our platform!"}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
